import { useCallback } from 'react';
import { Link } from 'react-router-dom';
import { routerConst } from '@ewf/lib/router';
import { WorkerApi } from '@shared/types/api';
import { useRemoteResourcesStore } from '@ewf/stores/remote-resources';
import { useExperimentalMode } from '@ewf/stores/experimental-mode';
import { useSolutionGroupsStore } from '@ewf/stores/solution-groups';
import { SortBy } from './SortBy';
import { WorkerCard, Props as WorkerCardProps } from './WorkerCard';
import { FilterBy } from './FilterBy';

export const WorkerList = ({ isLoading, workers }: Props): JSX.Element => {
  const { whitelistedSolutionGroupIds } = useRemoteResourcesStore();
  const { isExperimental } = useExperimentalMode();
  const { sortBy, filterBy, setSortBy, setFilterBy } = useSolutionGroupsStore();

  const filterWorkers = useCallback(
    (workers: WorkerApi.GetSolutionGroups): WorkerApi.GetSolutionGroups => {
      const UN_AUDITED_CATEGORY_KEY = 'energy web launchpad';

      return workers.filter((worker) => {
        // Pool classification
        const category = worker.publisherInfo?.toLowerCase() || 'other';
        const isOfficial = whitelistedSolutionGroupIds.includes(worker.id);
        const isUnAudited = category.endsWith(UN_AUDITED_CATEGORY_KEY);
        const isExpired = worker.isExpired;

        // Inclusion rules
        const alwaysShow = isOfficial;
        const showIfFilterEnabled = isExperimental
          ? !isOfficial // Experimental: show all non-official
          : isUnAudited; // Standard: show only un-audited
        const shouldInclude = alwaysShow || (filterBy.unAuditedPools && showIfFilterEnabled);

        // Exclusion rules
        const shouldExclude = !filterBy.expiredPools && isExpired;

        return shouldInclude && !shouldExclude;
      });
    },
    [filterBy, isExperimental, whitelistedSolutionGroupIds],
  );

  const sortWorkers = useCallback(
    (workers: WorkerApi.GetSolutionGroups): WorkerApi.GetSolutionGroups => {
      const { criteria, order } = sortBy;
      if (criteria === 'name') {
        return workers.sort((a, b) => {
          const aFill = a.name.toLowerCase();
          const bFill = b.name.toLowerCase();
          if (order === 'ascending') {
            if (aFill < bFill) return -1;
            if (aFill > bFill) return 1;
            return 0;
          } else {
            if (aFill > bFill) return -1;
            if (aFill < bFill) return 1;
            return 0;
          }
        });
      }
      if (criteria === 'date') {
        return workers.sort((a, b) => {
          const aDate = parseInt(a.id);
          const bDate = parseInt(b.id);
          if (order === 'ascending') {
            return aDate - bDate;
          }
          return bDate - aDate;
        });
      }
      if (criteria === 'time-remaining') {
        return workers.sort((a, b) => {
          const aTimeRemaining = parseInt(a.id);
          const bTimeRemaining = parseInt(b.id);
          if (order === 'ascending') {
            return aTimeRemaining - bTimeRemaining;
          }
          return bTimeRemaining - aTimeRemaining;
        });
      }
      if (criteria === 'min-staking-amount') {
        return workers.sort((a, b) => {
          const aMinStakeAmount = parseInt(a.id);
          const bMinStakeAmount = parseInt(b.id);
          if (order === 'ascending') {
            return aMinStakeAmount - bMinStakeAmount;
          }
          return bMinStakeAmount - aMinStakeAmount;
        });
      }
      return workers;
    },
    [sortBy],
  );

  const workerToWorkerCard = (worker: any): WorkerCardProps => ({
    id: worker.id,
    category: worker.publisherInfo,
    name: worker.name,
    description: worker.description,
    isOfficial: whitelistedSolutionGroupIds.includes(worker.id),
    hasOperatorsAllowlist: !!worker.hasOperatorsAllowlist,
    isExpired: worker.isExpired,
    status: {
      isFavourites: worker.isFavourites,
    },
  });

  const filteredAndSortedWorkers = sortWorkers(filterWorkers(workers));

  const Workers = (): React.ReactNode => (
    <div className="grid grid-cols-2 gap-4">
      {filteredAndSortedWorkers.map(workerToWorkerCard).map((workerCard) => (
        <Link
          key={workerCard.id}
          to={`${routerConst.SolutionDetail}/${workerCard.id}`}
          className="min-h-[280px]"
        >
          <WorkerCard {...workerCard} />
        </Link>
      ))}
    </div>
  );

  const LoadingWorkers = (): React.ReactNode => {
    const skeletonWorkers = Array.from({ length: 2 });
    return (
      <div className="grid grid-cols-2 gap-4">
        {skeletonWorkers.map((_, i) => (
          <div key={i} className="h-[280px] animate-pulse rounded-lg bg-gray-80" />
        ))}
      </div>
    );
  };

  const EmptyWorkers = (): React.ReactNode => (
    <div className="grid place-items-center rounded-lg bg-gray-80 p-8">
      <p className="text-subtle text-lg">No solution group found</p>
    </div>
  );

  const renderContent = (): React.ReactNode => {
    if (isLoading) return <LoadingWorkers />;
    if (filteredAndSortedWorkers.length === 0) return <EmptyWorkers />;
    return <Workers />;
  };

  return (
    <div className="rounded-lg bg-gray-90 p-6 shadow">
      <div className="mb-4 flex gap-4">
        <SortBy sortBy={sortBy} onValueChanged={setSortBy} />
        <FilterBy filterBy={filterBy} onValueChanged={setFilterBy} />
      </div>
      {renderContent()}
    </div>
  );
};

interface Props {
  isLoading: boolean;
  workers: WorkerApi.GetSolutionGroups;
}
